import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";

export const DRAFT = "DRAFT";
export const READY_TO_REVIEW = "READY_TO_REVIEW";
export const REVIEWED = "REVIEWED";
export const PUBLISHED = "PUBLISHED";
export const DISCARDED = "DISCARDED";
export const DISABLED = "DISABLED";

const AdminWordWheelBoardService = {
    requestList: async (paginationModel, sort, setRows, setRowCount, keyword, state, selectedGenreId, language, tags, free, difficultyLevel, season) => {
        const param = {
            page: paginationModel.page,
            pageSize: paginationModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
            keyword: keyword ? encodeURIComponent(keyword) : "",
        };

        if (state) {
            param.state = state;
        }

        if (selectedGenreId && selectedGenreId !== "all") {
            param.genreId = selectedGenreId;
        }

        if (language) {
            param.language = language;
        }

        if (tags && Array.isArray(tags) && tags.length > 0) {
            const tagStrings = tags
                .map((tag) => {
                    if (typeof tag === "string") return tag;
                    return tag.tag || String(tag);
                })
                .map((tag) => tag.toLowerCase().trim())
                .filter((tag) => tag.length > 0);
            param.tags = tagStrings.join(",");
        }

        if (free !== null && free !== undefined) {
            param.free = free === true || free === "true";
        }

        if (difficultyLevel && difficultyLevel !== "all") {
            param.difficultyLevel = difficultyLevel;
        }

        if (season) {
            param.season = season;
        }

        const result = await ApiService.getData("/v1/puzzle/wordwheel-tpl", param);
        setRows(result.content || []);
        const totalCount = result.page?.totalElements ?? 0;
        if (totalCount === 0 && result.content && result.content.length > 0) {
            Logger.warn("API response missing totalElements. Response structure:", result);
        }
        setRowCount(totalCount);
        return result;
    },

    requestTemplate: async (id) => ApiService.getData(`/v1/puzzle/wordwheel-tpl/${id}`),

    /**
     * Oldest reviewed templates first (id ASC). Used by Quick Publisher to walk the REVIEWED queue.
     */
    fetchReviewedQueue: async ({ language, pageSize = 20, excludeId, excludeIds, season } = {}) => {
        let content = [];
        let total = 0;
        const seasonFilter = season && season !== "all" ? season : null;
        await AdminWordWheelBoardService.requestList(
            { page: 0, pageSize },
            [{ field: "id", sort: "asc" }],
            (rows) => {
                content = rows || [];
            },
            (count) => {
                total = count ?? 0;
            },
            "",
            REVIEWED,
            "all",
            language,
            [],
            null,
            "all",
            seasonFilter
        );
        const skip = new Set(
            [excludeId, ...(Array.isArray(excludeIds) ? excludeIds : [])].filter(Boolean)
        );
        const filtered = skip.size
            ? content.filter((row) => row && !skip.has(row.id))
            : content.filter(Boolean);
        const next = filtered[0] || null;
        return { next, total, content: filtered };
    },

    updateTplState: async (id, newState, details = null) => {
        try {
            const payload = { state: newState };
            if (details) {
                payload.details = details;
            }
            const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${id}/state`, payload);
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to update puzzle state");
            }
            return result;
        } catch (error) {
            Logger.error(`Error updating word wheel state for ID ${id}:`, error);
            throw error;
        }
    },

    setClue: async (word, definition, puzzleId) => {
        try {
            return await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${puzzleId}/set-clue`, {
                definition,
            });
        } catch (error) {
            Logger.error(`Error setting title for word wheel ${puzzleId}:`, error);
            throw error;
        }
    },

    updatePuzzleCoordinates: async (puzzleId, filledCoordinates) => {
        try {
            return await ApiService.putData("/v1/puzzle/wordwheel-tpl/filled_coords", {
                wordWheelTplId: puzzleId,
                updatedCoords: JSON.stringify(filledCoordinates),
            });
        } catch (error) {
            Logger.error(`Error updating word wheel coordinates for ${puzzleId}:`, error);
            throw error;
        }
    },

    markAsDiscarded: async (tplId) => {
        return AdminWordWheelBoardService.updateTplState(tplId, DISCARDED);
    },

    publishTpl: async (id, publishComment, details, isFree, makeNow = true, options = {}) => {
        try {
            const payload = {
                comment: publishComment,
                details,
                isFree,
                makeNow: Boolean(makeNow),
            };
            if (Array.isArray(options.tags)) {
                payload.tags = options.tags;
            }
            return await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${id}/publish`, payload);
        } catch (error) {
            Logger.error(`Error publishing word wheel template ${id}:`, error);
            throw error;
        }
    },

    publishTemplates: async (items, makeNow = true) => {
        try {
            const payload = {
                makeNow: Boolean(makeNow),
                items: items.map((item) => {
                    const entry = {
                        id: item.id,
                        comment: item.comment ?? "",
                        details: item.details ?? null,
                        isFree: item.isFree ?? false,
                    };
                    if (Array.isArray(item.tags)) {
                        entry.tags = item.tags;
                    }
                    return entry;
                }),
            };
            return await ApiService.putData("/v1/puzzle/wordwheel-tpl/publish", payload);
        } catch (error) {
            Logger.error("Error bulk publishing word wheel templates:", error);
            throw error;
        }
    },

    assignSeasonLevels: async ({ season, language } = {}) => {
        try {
            const payload = { season };
            if (language && language !== "all") {
                payload.language = language;
            }
            const result = await ApiService.putData("/v1/puzzle/wordwheel-tpl/assign-season-levels", payload);
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to assign season levels");
            }
            return result;
        } catch (error) {
            Logger.error("Error assigning season levels:", error);
            throw error;
        }
    },

    updateTemplateInfo: async (tplId, { title, isFree, tags, details } = {}) => {
        try {
            const payload = {};
            if (title != null) payload.title = title;
            if (isFree != null) payload.isFree = isFree;
            if (tags != null) payload.tags = tags;
            if (details != null) payload.details = details;
            const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/info`, payload);
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to update template info");
            }
            return result;
        } catch (error) {
            Logger.error(`Error updating word wheel template info ${tplId}:`, error);
            throw error;
        }
    },

    setFree: async (tplId, isFree) => {
        try {
            return await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/set-free/${!!isFree}`);
        } catch (error) {
            Logger.error(`Error setting free for word wheel template ${tplId}:`, error);
            throw error;
        }
    },

    removeWord: async (tplId, word) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/remove-word`, { word });
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to remove word");
            }
            return result;
        } catch (error) {
            Logger.error(`Error removing word from word wheel template ${tplId}:`, error);
            throw error;
        }
    },

    /** Rebuild grid so words cross on the 8×8 board (wordsInUse + wordsNotInUse). */
    rearrangeWords: async (tplId) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/rearrange`, {});
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to rearrange words");
            }
            return result;
        } catch (error) {
            Logger.error(`Error rearranging word wheel template ${tplId}:`, error);
            throw error;
        }
    },

    /** Move this puzzle to a new catalog level and shift neighbors in the same season. */
    switchPuzzleLevel: async (tplId, puzzleLevel) => {
        try {
            const result = await ApiService.putData(`/v1/puzzle/wordwheel-tpl/${tplId}/switch-level`, {
                puzzleLevel,
            });
            if (result && result.code === "FAILURE") {
                throw new Error(result.message || "Failed to switch puzzle level");
            }
            return result;
        } catch (error) {
            Logger.error(`Error switching word wheel template level ${tplId}:`, error);
            throw error;
        }
    },
};

export default AdminWordWheelBoardService;
